import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingButton } from "@/components/loading-button";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { useUpdatePurchaseOrder } from "@/features/purchase-orders/hooks";
import { formatApiErrorMessage } from "@/lib/api-error";
import {
  ACCESSORIAL_FIELDS,
  accessorialSummary,
  readAccessorials,
  type AccessorialKey,
  type Accessorials,
} from "./accessorials-model";

type Choice = "true" | "false";

// The delivery site is picked under Ship to (USA-1465), so the dialog holds the extras only.
const FIELDS = ACCESSORIAL_FIELDS.filter(({ key }) => key !== "residential_delivery");

function toChoice(value: boolean | null): Choice | null {
  return value === null ? null : value ? "true" : "false";
}

function toDraft(values: Accessorials) {
  const next = {} as Record<AccessorialKey, Choice | null>;
  for (const { key } of FIELDS) next[key] = toChoice(values[key]);
  return next;
}

export function POAccessorialsControl({
  poUuid,
  snapshot,
  onSaved,
}: {
  poUuid: string;
  snapshot: Record<string, unknown>;
  onSaved?: () => void;
}) {
  const updatePO = useUpdatePurchaseOrder(poUuid);
  const current = { ...readAccessorials(snapshot), residential_delivery: null };
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(current));

  const complete = FIELDS.every(({ key }) => draft[key] !== null);
  const changed = FIELDS.some(
    ({ key }) => draft[key] !== toChoice(current[key]),
  );

  const openEditor = () => {
    setDraft(toDraft(current));
    setOpen(true);
  };

  const save = () => {
    const accessorials = {} as Partial<Record<AccessorialKey, boolean>>;
    for (const { key } of FIELDS)
      accessorials[key] = draft[key] === "true";
    updatePO.mutate(
      { accessorials },
      {
        onSuccess: () => {
          toast.success("Accessorials saved — PO regenerated");
          setOpen(false);
          onSaved?.();
        },
        onError: (error) =>
          toast.error(
            formatApiErrorMessage("Failed to save accessorials", error),
          ),
      },
    );
  };

  return (
    <>
      <dt className="pt-1 text-xs text-muted-foreground">Accessorials</dt>
      <dd className="min-w-0">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-auto min-h-7 max-w-full justify-start whitespace-normal px-2 py-1 text-left text-sm font-medium"
          onClick={openEditor}
          aria-label={`Edit accessorials: ${accessorialSummary(current)}`}
        >
          {accessorialSummary(current)}
          <Pencil
            className="size-3 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </dd>
      <Dialog
        open={open}
        onOpenChange={(next) => !updatePO.isPending && setOpen(next)}
      >
        <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Accessorials</DialogTitle>
          </DialogHeader>
          <div className="divide-y">
            {FIELDS.map(({ key, label, yes, no }) => (
              <div
                key={key}
                className="flex flex-col gap-1.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <span className="text-sm font-medium">{label}</span>
                <ReviewChoiceGroup
                  label={label}
                  value={draft[key]}
                  options={[
                    { value: "false", label: no },
                    { value: "true", label: yes },
                  ]}
                  className="grid w-full grid-cols-2 sm:w-64"
                  optionClassName="w-full justify-center"
                  disabled={updatePO.isPending}
                  onChange={(value) =>
                    setDraft((prev) => ({ ...prev, [key]: value }))
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={updatePO.isPending}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={updatePO.isPending}
              loadingText="Saving…"
              disabled={!complete || !changed}
              onClick={save}
            >
              Save
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
